import { ColorPicker } from '../../../../../../../widgets/colorPicker';
import { Dialog } from '../../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../../widgets/formControl';
import { Link } from '../../../../../../../widgets/buttons';
import React from 'react';
import Select from 'react-select';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import {
    ComposerMaterialPropertyType,
    ComposerMaterialsColorType,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    GetActionValueFromType
} from '../../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getCapitalizedFormattedName } from '../../helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../helpers/composerNodesAdapter';
import { useMaterialPropertyDetector } from '../../helpers/materialPropertyDetector';

// SHOW_OBJECTS = 2, // MPlaza.SceneActionType.ShowHideMesh,
// HIDE_OBJECTS = 2, //MPlaza.SceneActionType.ShowHideMesh,
// SET_OBJECT_MATERIAL = 3, //MPlaza.SceneActionType.SetMeshMaterial,
// SET_SUB_OBJECT_MATERIAL = 7, //MPlaza.SceneActionType.SetSubMeshMaterial,
// SET_MATERIAL_COLOR = 4, //MPlaza.SceneActionType.SetMaterialColor,
// MOVE_CAMERA = 5, //MPlaza.SceneActionType.SetCameraLocation,
// SET_DESIGN_MATERIAL = 11, //MPlaza.SceneActionType.SetMeshDesignMaterial,
// SET_CAMERA_PIVOT = 8, //MPlaza.SceneActionType.SetCameraPivot,
// RESET_CAMERA_PIVOT = 9 //MPlaza.SceneActionType.ResetCameraPivot
const ComposerActionsValues = Object.values(ComposerOptionActionsType).filter((value) => typeof value === 'number');
const ComposerActionsTypeArray = ComposerActionsValues as ComposerOptionActionsType[];
const typeSelector = <T,>(type: ComposerOptionActionsType, ...choices: T[]): T => {
    const index = ComposerActionsTypeArray.indexOf(type);
    return choices[index] || choices[0];
};

// Helper function per estrarre il valore corretto dalle action
const getActionValue = (parsedAction: any, actionType: ComposerOptionActionsType): string => {
    const rawValue = parsedAction?.get('value') as string;
    // Con la nuova struttura (dot notation), il valore è già diretto, non serve parsing JSON
    return rawValue || '';
};

const ComposerActionsSelect = ComposerActionsValues.map((value, i) => ({
    label: getCapitalizedFormattedName(ComposerOptionActionsType[value]),
    value: value
}));

const ComposerActionsMaterialColors = Object.entries(ComposerMaterialsColorType).map((key) => {
    return { label: key[0], value: key[1] };
});
const ComposerSaveOptionDialogWindow = styled(Dialog)`
    max-width: 600px;
    position: static;
    overflow-y: show;
`;

const ComposerSaveOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    // min-height: 500px;
    overflow-y: show;
    align-items: center;
    padding: 2em;
    justify-content: start;
    position: static;
`;

const StyledFormControl = styled(FormControl)`
    overflow: visible;
`;

interface ComposerSaveActionProps {
    onConfirm: (action: ComposerOptionActionNode | null) => void;
    passedAction?: ComposerOptionActionNode | null;
    optionID: string;
    attributeID: string;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
    edit?: boolean;
}

const ComposerSaveActionDialog: React.FC<ComposerSaveActionProps> = ({ onConfirm, passedAction, optionID, attributeID, edit, meshes, materials, cameras }) => {
    const [parsedAction, setParsedAction] = React.useState<MPlaza.AttributeSceneAction>(ComposerAttributeNodeAdapter.fromActionNode(passedAction || null));
    const [actionType, setActionType] = React.useState<ComposerOptionActionsType>(passedAction?.type ?? ComposerOptionActionsType.SHOW_OBJECTS);

    // Stati per SET_MATERIAL_PARAMETER
    const [selectedMaterial, setSelectedMaterial] = React.useState<BABYLON.Material | null>(() => {
        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            const materialId = parsedAction?.get('selector');
            const material = materials?.find((m) => m.id === materialId) || null;

            // CASO SPECIALE: Se l'action salvata usa clearCoat_textureRoughnessTexture,
            // dobbiamo forzare il flag useRoughnessFromMainTexture a false PRIMA che l'hook venga chiamato
            const propertyPath = parsedAction?.get('propertyName') as string;
            if (material && propertyPath === 'clearCoat_textureRoughnessTexture') {
                const mat = material as any;
                if (mat.clearCoat) {
                    console.log('🔧 Initializing material with useRoughnessFromMainTexture = false for texture property');
                    mat.clearCoat.useRoughnessFromMainTexture = false;
                }
            }

            return material;
        }
        return null;
    });

    // Stati per la selezione delle proprietà
    const [selectedProperty, setSelectedProperty] = React.useState<string>('');
    const [selectedSubProperty, setSelectedSubProperty] = React.useState<{ value: string; type: ComposerMaterialPropertyType; label?: string } | null>(null);
    const [selectedSubSubProperty, setSelectedSubSubProperty] = React.useState<{ value: string; type: ComposerMaterialPropertyType; label?: string } | null>(
        null
    );
    const [propertyName, setPropertyName] = React.useState<string>('');

    // Usa l'hook MaterialPropertyDetector per ottenere tutte le proprietà disponibili e leggere i valori reali
    const materialPropertyHook = useMaterialPropertyDetector(selectedMaterial);
    const { getAvailablePropertyGroups, renderPropertyControl, hasSubSubProperties, getSubSubProperties } = materialPropertyHook;

    // Flag per controllare che l'inizializzazione avvenga solo UNA VOLTA all'apertura della dialog
    const hasInitializedFromAction = React.useRef(false);

    // Ricostruisci dinamicamente gli stati quando editi un'action esistente
    // IMPORTANTE: Questo useEffect deve eseguire SOLO all'apertura della dialog, non ad ogni cambio
    React.useEffect(() => {
        console.log('🔵 useEffect triggered', {
            hasInitialized: hasInitializedFromAction.current,
            hasPassedAction: !!passedAction,
            actionType: passedAction?.type,
            hasParsedAction: !!parsedAction,
            hasMaterial: !!selectedMaterial,
            propertyGroupsCount: getAvailablePropertyGroups.length
        });

        // Se abbiamo già inizializzato, non fare nulla
        if (hasInitializedFromAction.current) {
            console.log('⏭️ Already initialized, skipping');
            return;
        }

        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER && parsedAction && selectedMaterial) {
            const propertyPath = parsedAction.get('propertyName') as string;

            if (!propertyPath || !getAvailablePropertyGroups || getAvailablePropertyGroups.length === 0) {
                console.log('⚠️ Cannot initialize: missing propertyPath or no property groups available', {
                    propertyPath,
                    groupsLength: getAvailablePropertyGroups?.length
                });
                return;
            }

            console.log('🔄 Initializing action from propertyPath:', propertyPath);
            console.log('📋 Available property groups:', getAvailablePropertyGroups.length);

            // Parse della dot notation per ricostruire le selezioni
            // Formato NORMALIZZATO: "{propertyGroup}.{subProperty}" o "{propertyGroup}.{subProperty}.{subSubProperty}"
            // Esempi:
            //   - "albedo.albedoColor"
            //   - "clearCoat.useRoughnessFromMainTexture"
            //   - "anisotropic.texture"
            //   - "subsurface.thicknessTexture"
            // Formato LEGACY (con underscore):
            //   - "clearCoat_textureRoughnessTexture"
            //   - "anisotropy_texture"

            let parsedPropertyGroup = '';
            let parsedSubProperty = '';

            // Se contiene un punto, è il formato con dot notation
            if (propertyPath.includes('.')) {
                const parts = propertyPath.split('.');
                parsedPropertyGroup = parts[0].toLowerCase(); // normalizza a lowercase
                parsedSubProperty = parts.slice(1).join('.'); // resto del path
                console.log('🔧 Parsed dot notation:', {
                    parsedPropertyGroup,
                    parsedSubProperty,
                    fullPath: propertyPath,
                    parts: parts
                });
            } else if (propertyPath.includes('_')) {
                // Formato legacy con underscore: "{group}_{property}"
                const parts = propertyPath.split('_');
                parsedPropertyGroup = parts[0].toLowerCase(); // es. "clearCoat" → "clearcoat"
                parsedSubProperty = parts.slice(1).join('_'); // resto con underscore
                console.log('🔧 Parsed legacy underscore format:', {
                    parsedPropertyGroup,
                    parsedSubProperty,
                    fullPath: propertyPath
                });
            }

            // Cerca in tutti i property groups disponibili
            for (const propGroup of getAvailablePropertyGroups) {
                console.log(`  🔍 Checking property group: ${propGroup.value}`);

                // Se abbiamo parsato il property group, cerca solo quello specifico
                if (parsedPropertyGroup && propGroup.value.toLowerCase() !== parsedPropertyGroup.toLowerCase()) {
                    continue;
                }

                // Cerca nelle sub-properties
                if (propGroup.subProperties) {
                    for (const subProp of propGroup.subProperties) {
                        // Match con dot notation: confronta la parte dopo il punto
                        if (parsedSubProperty && subProp.value === parsedSubProperty) {
                            console.log(`  ✅ Found match via dot notation: ${propGroup.value}.${subProp.value}`);
                            setSelectedProperty(propGroup.value);
                            setSelectedSubProperty(subProp);
                            setPropertyName(parsedSubProperty); // Salva solo la sub-property per compatibilità UI
                            hasInitializedFromAction.current = true;
                            return;
                        }

                        // Match diretto sulla subProperty (formato legacy)
                        if (subProp.value === propertyPath) {
                            console.log(`  ✅ Found direct match in subProperty: ${subProp.value}`);
                            setSelectedProperty(propGroup.value);
                            setSelectedSubProperty(subProp);
                            setPropertyName(propertyPath);
                            hasInitializedFromAction.current = true;
                            return;
                        }

                        // Se ha sub-sub-properties, cerca anche lì
                        if (hasSubSubProperties(propGroup.value, subProp.value)) {
                            const subSubProps = getSubSubProperties(propGroup.value, subProp.value);
                            console.log(`    🔍 Checking ${subSubProps.length} sub-sub-properties under ${propGroup.value}.${subProp.value}`);
                            console.log(`    🔍 Looking for propertyPath: "${propertyPath}" or parsedSubProperty: "${parsedSubProperty}"`);

                            for (const subSubProp of subSubProps) {
                                // Per dot notation: costruisci il full path della sub-sub-property e confronta
                                // Esempio: propertyPath = "clearcoat.useRoughnessFromMainTexture"
                                //          subSubProp.value potrebbe essere "clearCoat.useRoughnessFromMainTexture"
                                //          oppure solo "useRoughnessFromMainTexture"

                                // Match con il full path originale
                                if (subSubProp.value === propertyPath) {
                                    setSelectedProperty(propGroup.value);
                                    setSelectedSubProperty(subProp);
                                    setSelectedSubSubProperty(subSubProp);
                                    setPropertyName(parsedSubProperty); // Salva solo la parte dopo il punto
                                    hasInitializedFromAction.current = true;
                                    return;
                                } // Match con la parte dopo il punto (parsedSubProperty)
                                // Questo gestisce il caso dove la sub-sub-property contiene la dot notation completa
                                // es. subSubProp.value = "clearCoat.useRoughnessFromMainTexture" e parsedSubProperty = "useRoughnessFromMainTexture"
                                if (parsedSubProperty && subSubProp.value.includes('.')) {
                                    const subSubPropParts = subSubProp.value.split('.');
                                    const subSubPropName = subSubPropParts[subSubPropParts.length - 1]; // ultima parte
                                    if (subSubPropName === parsedSubProperty) {
                                        setSelectedProperty(propGroup.value);
                                        setSelectedSubProperty(subProp);
                                        setSelectedSubSubProperty(subSubProp);
                                        setPropertyName(parsedSubProperty);
                                        hasInitializedFromAction.current = true;
                                        return;
                                    }
                                }

                                // Match semplice con parsedSubProperty per backward compatibility
                                if (parsedSubProperty && subSubProp.value === parsedSubProperty) {
                                    setSelectedProperty(propGroup.value);
                                    setSelectedSubProperty(subProp);
                                    setSelectedSubSubProperty(subSubProp);
                                    setPropertyName(parsedSubProperty);
                                    hasInitializedFromAction.current = true;
                                    return;
                                }
                            }
                        }
                    }
                }
            }

            // CASI SPECIALI: Gestione texture per clearCoat, sheen, anisotropic, subSurface

            // ==================== CLEARCOAT ROUGHNESS ====================
            const isClearCoatRoughness =
                propertyPath === 'clearCoat.useRoughnessFromMainTexture' ||
                propertyPath === 'clearCoat_textureRoughnessTexture' ||
                propertyPath === 'clearcoat.useRoughnessFromMainTexture' ||
                propertyPath === 'clearcoat.clearCoat.useRoughnessFromMainTexture' ||
                (propertyPath.includes('clearCoat') && propertyPath.includes('useRoughnessFromMainTexture')) ||
                (propertyPath.includes('clearCoat') && propertyPath.includes('RoughnessTexture'));

            if (isClearCoatRoughness) {
                const clearCoatGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'clearcoat');
                if (clearCoatGroup) {
                    const roughnessSection = clearCoatGroup.subProperties.find((sp) => sp.value === 'clearCoat_roughness_section');
                    if (roughnessSection) {
                        setSelectedProperty('clearcoat');
                        setSelectedSubProperty(roughnessSection);

                        const subSubProps = getSubSubProperties('clearcoat', 'clearCoat_roughness_section');
                        const checkboxOption = subSubProps.find((ssp) => ssp.value === 'clearCoat.useRoughnessFromMainTexture');
                        if (checkboxOption) {
                            setSelectedSubSubProperty(checkboxOption);
                        }

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        const actionValue = parsedAction?.get('value') as string;
                        if (propertyPath === 'clearCoat.useRoughnessFromMainTexture') {
                            setUseRoughnessFromMainTexture(actionValue === 'true');
                        } else {
                            setUseRoughnessFromMainTexture(false);
                        }

                        hasInitializedFromAction.current = true;
                        console.log('✅ Initialized clearCoat roughness from action');
                        return;
                    }
                }
            }

            // ==================== SHEEN ROUGHNESS/TEXTURE ====================
            const isSheenProperty =
                propertyPath.includes('sheen') &&
                (propertyPath.includes('roughness') ||
                    propertyPath.includes('texture') ||
                    propertyPath.includes('useRoughnessFromMainTexture') ||
                    propertyPath.includes('Roughness'));

            if (isSheenProperty) {
                const sheenGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'sheen');
                if (sheenGroup) {
                    // Cerca la sub-property appropriata
                    let targetSubProp = null;

                    // Se è una texture generica sheen
                    if (propertyPath.includes('sheen_texture') || propertyPath.includes('sheen.texture')) {
                        targetSubProp = sheenGroup.subProperties.find((sp) => sp.value === 'sheen_texture');
                    }
                    // Se è roughness texture o checkbox
                    else if (propertyPath.includes('roughness') || propertyPath.includes('Roughness')) {
                        // Cerca una section per roughness (simile a clearCoat)
                        targetSubProp = sheenGroup.subProperties.find((sp) => sp.value.includes('roughness') || sp.value.includes('Roughness'));
                    }

                    if (targetSubProp) {
                        setSelectedProperty('sheen');
                        setSelectedSubProperty(targetSubProp);

                        // Se ha sub-sub-properties, cerca quella corretta
                        if (hasSubSubProperties('sheen', targetSubProp.value)) {
                            const subSubProps = getSubSubProperties('sheen', targetSubProp.value);
                            const matchingSubSub = subSubProps.find(
                                (ssp) =>
                                    ssp.value === propertyPath ||
                                    ssp.value === parsedSubProperty ||
                                    (parsedSubProperty && ssp.value.includes(parsedSubProperty))
                            );
                            if (matchingSubSub) {
                                setSelectedSubSubProperty(matchingSubSub);
                            }
                        }

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('✅ Initialized sheen property from action:', propertyPath);
                        return;
                    }
                }
            }

            // ==================== ANISOTROPIC TEXTURE ====================
            const isAnisotropicTexture =
                (propertyPath.includes('anisotropy') && propertyPath.includes('texture')) ||
                (propertyPath.includes('anisotropic') && propertyPath.includes('texture')) ||
                propertyPath === 'anisotropy_texture' ||
                propertyPath === 'anisotropic.texture';

            if (isAnisotropicTexture) {
                const anisotropicGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'anisotropic' || pg.value === 'anisotropy');
                if (anisotropicGroup) {
                    const textureSubProp = anisotropicGroup.subProperties.find((sp) => sp.value.includes('texture') || sp.value === 'anisotropy_texture');
                    if (textureSubProp) {
                        setSelectedProperty(anisotropicGroup.value);
                        setSelectedSubProperty(textureSubProp);

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('✅ Initialized anisotropic texture from action');
                        return;
                    }
                }
            }

            // ==================== SUBSURFACE TEXTURES ====================
            const isSubSurfaceTexture =
                (propertyPath.includes('subSurface') && propertyPath.includes('texture')) ||
                (propertyPath.includes('subsurface') && propertyPath.includes('Texture')) ||
                (propertyPath.startsWith('subSurface_') && propertyPath.includes('Texture'));

            if (isSubSurfaceTexture) {
                const subSurfaceGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'subsurface' || pg.value === 'subSurface');
                if (subSurfaceGroup) {
                    // Cerca la sub-property che corrisponde alla texture specifica
                    // Es: subSurface_thicknessTexture, subSurface_refractionIntensityTexture, etc.
                    const textureSubProp = subSurfaceGroup.subProperties.find((sp) => {
                        // Match diretto
                        if (sp.value === propertyPath || sp.value === parsedSubProperty) {
                            return true;
                        }
                        // Match parziale - la sub-property contiene il nome della texture
                        if (parsedSubProperty && sp.value.includes(parsedSubProperty)) {
                            return true;
                        }
                        // Match per formato legacy con underscore
                        const legacyName = propertyPath.replace('.', '_');
                        if (sp.value === legacyName) {
                            return true;
                        }
                        return false;
                    });

                    if (textureSubProp) {
                        setSelectedProperty(subSurfaceGroup.value);
                        setSelectedSubProperty(textureSubProp);

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('✅ Initialized subSurface texture from action:', propertyPath);
                        return;
                    }
                }
            }
            getAvailablePropertyGroups.forEach((pg) => {
                pg.subProperties.forEach((sp) => {
                    console.log(`  - ${sp.value}`);
                    if (hasSubSubProperties(pg.value, sp.value)) {
                        getSubSubProperties(pg.value, sp.value).forEach((ssp) => {
                            console.log(`    - ${ssp.value}`);
                        });
                    }
                });
            });
        }
    }, [passedAction, parsedAction, selectedMaterial, getAvailablePropertyGroups, hasSubSubProperties, getSubSubProperties]);

    // Flag per la roughness checkbox - inizializza dal valore del materiale o dall'azione salvata
    const [useRoughnessFromMainTexture, setUseRoughnessFromMainTexture] = React.useState<boolean>(() => {
        // Se stiamo editando un'azione esistente
        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            const propertyPath = parsedAction?.get('propertyName') as string;
            const value = parsedAction?.get('value') as string;

            // Se l'azione salvata è per useRoughnessFromMainTexture, usa quel valore
            if (propertyPath === 'clearCoat.useRoughnessFromMainTexture') {
                return value === 'true';
            }

            // Se l'azione salvata è per la TEXTURE, allora useRoughnessFromMainTexture è FALSE
            if (propertyPath === 'clearCoat_textureRoughnessTexture') {
                return false;
            }
        }

        // Altrimenti leggi il valore attuale dal materiale selezionato
        if (selectedMaterial) {
            try {
                const material = selectedMaterial as any;
                if (material.clearCoat && typeof material.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                    return material.clearCoat.useRoughnessFromMainTexture;
                }
            } catch (e) {
                console.warn('Could not read clearCoat.useRoughnessFromMainTexture from material:', e);
            }
        }

        return true; // Default value
    });

    const handleConfirm = () => {
        // Validazione per SET_MATERIAL_PARAMETER
        if (actionType === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            // Validazioni obbligatorie
            if (!selectedMaterial) {
                alert(T._('Please select a material', 'Admin'));
                return;
            }

            if (!selectedProperty) {
                alert(T._('Please select a property group', 'Admin'));
                return;
            }

            if (!selectedSubProperty) {
                alert(T._('Please select a sub-property', 'Admin'));
                return;
            }

            // Se la sub-property ha sub-sub-properties, deve essere selezionata anche quella
            if (hasSubSubProperties(selectedProperty, selectedSubProperty.value) && !selectedSubSubProperty) {
                alert(T._('Please select a sub-sub-property', 'Admin'));
                return;
            }

            if (!propertyName) {
                alert(T._('Property name is not set. Please reselect the property.', 'Admin'));
                return;
            }

            const currentValue = getActionValue(parsedAction, actionType);
            if (!currentValue && currentValue !== '') {
                // Accetta stringa vuota come valore valido
                alert(T._('Please set a value for the property', 'Admin'));
                return;
            }

            // Imposta i valori finali nella parsedAction prima di salvare
            // NUOVA STRUTTURA: usa dot notation nel propertyName e solo il valore in value
            parsedAction?.set('selector', selectedMaterial.id);

            // Costruisci il propertyName completo con dot notation NORMALIZZATO
            // Formato finale: "{selectedProperty}.{actualPropertyName}"
            // Es: "clearcoat.useRoughnessFromMainTexture", "anisotropic.texture", "subsurface.thicknessTexture"

            let fullPropertyName = '';

            // Se propertyName contiene già un punto, potrebbe avere il prefisso duplicato
            if (propertyName.includes('.')) {
                // Rimuovi il prefisso selectedProperty se è duplicato
                // Es: se propertyName = "clearcoat.useRoughnessFromMainTexture" e selectedProperty = "clearcoat"
                //     oppure propertyName = "clearCoat.useRoughnessFromMainTexture" (case diverso)
                const normalizedPropertyName = propertyName.toLowerCase();
                const normalizedSelectedProperty = selectedProperty?.toLowerCase() || '';

                if (normalizedPropertyName.startsWith(normalizedSelectedProperty + '.')) {
                    // Il propertyName ha già il prefisso → usa as-is
                    fullPropertyName = propertyName;
                } else {
                    // Il propertyName ha un punto ma NON è il selectedProperty → concatena
                    fullPropertyName = selectedProperty ? `${selectedProperty}.${propertyName}` : propertyName;
                }
            } else {
                // propertyName NON ha il punto → concatena con selectedProperty
                fullPropertyName = selectedProperty ? `${selectedProperty}.${propertyName}` : propertyName;
            }

            parsedAction?.set('propertyName', fullPropertyName); // dot notation normalizzata
            parsedAction?.set('value', currentValue); // solo il valore: es. "#ffffff", "true", "https://texture.jpg"

            // CRITICAL: Assicurati che actionTypeID sia impostato correttamente

            const correctActionTypeID = GetActionValueFromType(ComposerOptionActionsType.SET_MATERIAL_PARAMETER);

            parsedAction?.set('actionTypeID', correctActionTypeID);

            console.log('💾 Saving SET_MATERIAL_PARAMETER action:', {
                actionTypeID: correctActionTypeID,
                selector: selectedMaterial.id,
                propertyName: fullPropertyName,
                originalPropertyName: propertyName,
                selectedProperty: selectedProperty,
                value: currentValue,
                parsedActionJSON: parsedAction?.toJSON()
            });
        }

        const finalNodeAction = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, parsedAction);
        console.log('💾 Final node action before confirm:', {
            type: finalNodeAction.type,
            typeName: ComposerOptionActionsType[finalNodeAction.type],
            selector: finalNodeAction.selector,
            property: finalNodeAction.property,
            value: finalNodeAction.value
        });
        onConfirm(finalNodeAction);
    };

    const title = edit ? T._('Edit Action', 'Admin') : T._('Create New Action', 'Admin');

    const setSelector = (value: string | undefined | null) => {
        parsedAction?.set('selector', value);
    };

    const updatePropertyName = (value: string | undefined | null) => {
        const newValue = value || '';
        setPropertyName(newValue);
        parsedAction?.set('propertyName', value);
    };

    const setValue = (value: string | undefined | null) => {
        // Logica speciale per roughness checkbox
        if (propertyName === 'clearCoat.useRoughnessFromMainTexture') {
            setUseRoughnessFromMainTexture(value === 'true');
        }

        // Per SET_MATERIAL_PARAMETER, salva solo il valore (non più JSON)
        parsedAction?.set('value', value || '');
    };

    const update = () => {
        setParsedAction((parsedAction) => parsedAction.clone());
    };

    const form = () => {
        switch (actionType) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
                //SHOW OBJECT
                return (
                    <>
                        <StyledFormControl label={T._('Show Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || T._('Mesh', 'Admin')
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    setValue('show');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </StyledFormControl>
                    </>
                );
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return (
                    <>
                        <FormControl label={T._('Hide Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected

                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    setValue('hide');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
                return (
                    <>
                        <FormControl label={T._('Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        <FormControl label={T._('Material', 'Admin')}>
                            <Select
                                options={
                                    materials?.map((material) => ({
                                        value: material.id,
                                        label: material.name || material.id
                                    })) ?? []
                                }
                                value={{
                                    value: (parsedAction?.get('value') as string) || '',
                                    label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                }}
                                onChange={(selected) => {
                                    setValue(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return (
                    <>
                        <FormControl label={T._('Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name || s })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        <FormControl label={T._('Material', 'Admin')}>
                            <Select
                                options={
                                    materials?.map((material) => ({
                                        value: material.id,
                                        label: material.name || material.id
                                    })) ?? []
                                }
                                value={{
                                    value: (parsedAction?.get('value') as string) || '',
                                    label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                }}
                                onChange={(selected) => {
                                    setValue(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return (
                    <>
                        <FormControl label={T._('Materials', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    materials?.map((material) => ({
                                        value: material.id,
                                        label: material.name || material.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: materials?.find((m) => m.id === s)?.name || s })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        <FormControl label={T._('Color type', 'Admin')}>
                            <Select
                                options={ComposerActionsMaterialColors}
                                value={ComposerActionsMaterialColors.find((option) => option.value === parsedAction?.get('propertyName')) || null}
                                onChange={(selected) => {
                                    updatePropertyName(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        <FormControl label={T._('Color value', 'Admin')}>
                            <ColorPicker
                                color={getActionValue(parsedAction, actionType)}
                                onChange={(color) => {
                                    setValue(color);
                                    update();
                                }}
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return (
                    <>
                        {/* STEP 1: Selezione Materiale */}
                        <FormControl label={T._('Material', 'Admin')}>
                            <Select
                                options={
                                    materials?.map((material) => ({
                                        value: material.id,
                                        label: material.name || material.id
                                    })) ?? []
                                }
                                value={
                                    selectedMaterial
                                        ? {
                                              value: selectedMaterial.id,
                                              label: selectedMaterial.name || selectedMaterial.id
                                          }
                                        : null
                                }
                                onChange={(selected) => {
                                    const material = materials?.find((m) => m.id === selected?.value) || null;
                                    setSelectedMaterial(material);
                                    setSelectedProperty(''); // Reset property selection
                                    setSelectedSubProperty(null); // Reset sub-property selection
                                    setSelectedSubSubProperty(null); // Reset sub-sub-property selection
                                    setValue('');
                                    setSelector(selected?.value || '');

                                    // Aggiorna useRoughnessFromMainTexture leggendo dal materiale
                                    if (material) {
                                        try {
                                            const mat = material as any;
                                            if (mat.clearCoat && typeof mat.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                                                setUseRoughnessFromMainTexture(mat.clearCoat.useRoughnessFromMainTexture);
                                            }
                                        } catch (e) {
                                            console.warn('Could not read clearCoat.useRoughnessFromMainTexture:', e);
                                        }
                                    }

                                    update();
                                }}
                                placeholder={T._('Select a material...', 'Admin')}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        {/* STEP 2: Selezione Proprietà (solo se materiale selezionato) */}
                        {selectedMaterial && (
                            <FormControl label={T._('Property', 'Admin')}>
                                <Select
                                    options={getAvailablePropertyGroups}
                                    value={getAvailablePropertyGroups.find((prop) => prop.value === selectedProperty) || null}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    isDisabled={getAvailablePropertyGroups.length === 0}
                                    onChange={(selected) => {
                                        const value = selected?.value || '';
                                        setSelectedProperty(value);
                                        setSelectedSubProperty(null);
                                        setSelectedSubSubProperty(null);
                                        setValue('');
                                    }}
                                    placeholder={T._('Select a property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {getAvailablePropertyGroups.length === 0 && (
                                    <div style={{ color: '#ff6b35', fontSize: '12px', marginTop: '4px' }}>
                                        {T._('No properties detected for this material', 'Admin')}
                                    </div>
                                )}
                                {getAvailablePropertyGroups.length > 0 && !selectedProperty && (
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                        {T._('Found', 'Admin')} {getAvailablePropertyGroups.length} {T._('properties available', 'Admin')}
                                    </div>
                                )}
                            </FormControl>
                        )}
                        {/* STEP 3: Selezione Sotto-proprietà (solo se proprietà selezionata) */}
                        {selectedMaterial && selectedProperty && (
                            <FormControl label={T._('Sub-property', 'Admin')}>
                                <Select
                                    options={getAvailablePropertyGroups.find((p) => p.value === selectedProperty)?.subProperties || []}
                                    value={
                                        getAvailablePropertyGroups
                                            .find((p) => p.value === selectedProperty)
                                            ?.subProperties.find((sp) => sp.value === selectedSubProperty?.value) || null
                                    }
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    onChange={(selected) => {
                                        if (selected) {
                                            setSelectedSubProperty({ value: selected.value, type: selected.type });
                                            setSelectedSubSubProperty(null);

                                            // Se la sub-property NON ha sub-sub-properties, imposta subito il propertyName
                                            if (!hasSubSubProperties(selectedProperty, selected.value)) {
                                                updatePropertyName(selected.value);
                                            }

                                            // IMPORTANTE: Quando cambio sub-property in ADD MODE (non edit),
                                            // devo sempre resettare i valori leggendoli dal materiale
                                            // Per clearCoat roughness, rileggi sempre il flag dal materiale
                                            if (selectedProperty === 'clearcoat' && selected.value === 'clearCoat_roughness_section') {
                                                if (selectedMaterial && !hasInitializedFromAction.current) {
                                                    try {
                                                        const mat = selectedMaterial as any;
                                                        if (mat.clearCoat && typeof mat.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                                                            console.log(
                                                                '🔄 Re-reading useRoughnessFromMainTexture from material:',
                                                                mat.clearCoat.useRoughnessFromMainTexture
                                                            );
                                                            setUseRoughnessFromMainTexture(mat.clearCoat.useRoughnessFromMainTexture);
                                                        }
                                                    } catch (e) {
                                                        console.warn('Could not read clearCoat.useRoughnessFromMainTexture:', e);
                                                    }
                                                }
                                            }

                                            // Reset del valore quando cambio proprietà (in add mode)
                                            // In edit mode, il valore verrà poi popolato dall'inizializzazione
                                            setValue('');
                                            update();
                                        }
                                    }}
                                    placeholder={T._('Select a sub-property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {selectedProperty && (
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                        {(() => {
                                            const selectedProp = getAvailablePropertyGroups.find((p) => p.value === selectedProperty);
                                            return selectedProp ? `${selectedProp.subProperties.length} ${T._('sub-properties available', 'Admin')}` : '';
                                        })()}
                                    </div>
                                )}
                            </FormControl>
                        )}
                        {/* STEP 4: Selezione Sub-Sub-proprietà (solo se sub-property ha sub-sub-properties) */}
                        {selectedMaterial && selectedProperty && selectedSubProperty && hasSubSubProperties(selectedProperty, selectedSubProperty.value) && (
                            <FormControl label={`${selectedSubProperty.value.split('_')[0]} ${T._('sub-property', 'Admin')}`}>
                                <Select
                                    options={getSubSubProperties(selectedProperty, selectedSubProperty.value)}
                                    value={
                                        getSubSubProperties(selectedProperty, selectedSubProperty.value).find(
                                            (ssp) => ssp.value === selectedSubSubProperty?.value
                                        ) || null
                                    }
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    onChange={(selected) => {
                                        if (selected) {
                                            setSelectedSubSubProperty({ value: selected.value, type: selected.type });
                                            updatePropertyName(selected.value);
                                            setValue('');
                                            update();
                                        }
                                    }}
                                    placeholder={T._('Select a sub-sub-property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {(() => {
                                    const subSubProps = getSubSubProperties(selectedProperty, selectedSubProperty.value);
                                    return (
                                        subSubProps.length > 0 && (
                                            <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                                {`${subSubProps.length} ${T._('sub-sub-properties available', 'Admin')}`}
                                            </div>
                                        )
                                    );
                                })()}
                            </FormControl>
                        )}{' '}
                        {/* STEP 5: Controllo Valore (per sub-sub-property selezionata o sub-property senza sub-sub-properties) */}
                        {(selectedSubSubProperty || (selectedSubProperty && !hasSubSubProperties(selectedProperty, selectedSubProperty.value))) &&
                            (() => {
                                const currentProperty = selectedSubSubProperty || selectedSubProperty;
                                if (!currentProperty) return null;

                                // Per clearCoat roughness, passa uno state object speciale che include entrambi i valori
                                // IMPORTANTE: Se siamo nella sezione roughness, SEMPRE passa lo state object!
                                const isRoughnessProperty = selectedProperty === 'clearcoat' && selectedSubProperty?.value === 'clearCoat_roughness_section';

                                let valueToPass: string;
                                if (isRoughnessProperty) {
                                    // Per roughness, costruisci un oggetto JSON con entrambi i valori
                                    const rawTextureValue = getActionValue(parsedAction, actionType);
                                    // Se la checkbox è abilitata O se il valore è "false" (stringa), svuota la texture
                                    const textureValue = useRoughnessFromMainTexture || rawTextureValue === 'false' ? '' : rawTextureValue;

                                    const stateObject = {
                                        checkboxValue: useRoughnessFromMainTexture,
                                        textureValue: textureValue
                                    };
                                    valueToPass = JSON.stringify(stateObject);
                                } else {
                                    valueToPass = getActionValue(parsedAction, actionType);
                                }

                                return renderPropertyControl(
                                    {
                                        label: currentProperty.value.split('.').pop() || currentProperty.value,
                                        value: currentProperty.value,
                                        type: currentProperty.type
                                    },
                                    valueToPass,
                                    (newValue) => {
                                        // Gestione speciale per clearCoat roughness
                                        // Il detector ritorna valori prefissati con 'checkbox:' o 'texture:'
                                        if (newValue.startsWith('checkbox:')) {
                                            // Rimuovi il prefisso e controlla se c'è il flag clearTexture
                                            const parts = newValue.replace('checkbox:', '').split('|');
                                            const actualValue = parts[0];
                                            const shouldClearTexture = parts.includes('clearTexture');

                                            // NON switchare mai la selectedSubSubProperty - mantieni sempre selezionata nella UI
                                            // Ma aggiorna il propertyName per salvare il valore corretto
                                            updatePropertyName('clearCoat.useRoughnessFromMainTexture');

                                            // Aggiorna il flag locale
                                            setUseRoughnessFromMainTexture(actualValue === 'true');
                                            setValue(actualValue);
                                            update();
                                        } else if (newValue.startsWith('texture:')) {
                                            const actualValue = newValue.replace('texture:', '');

                                            // IMPORTANTE: Quando scrivi nella texture, disabilita sempre la checkbox
                                            setUseRoughnessFromMainTexture(false);

                                            // NON switchare la selectedSubSubProperty - mantieni sempre selezionata la checkbox nella UI
                                            // Ma aggiorna il propertyName per salvare il valore della texture
                                            updatePropertyName('clearCoat_textureRoughnessTexture');

                                            setValue(actualValue);
                                            update();
                                        } else {
                                            // Caso normale, nessun prefisso
                                            setValue(newValue);
                                            update();
                                        }
                                    }
                                );
                            })()}
                    </>
                );
            case ComposerOptionActionsType.MOVE_CAMERA:
                return (
                    <>
                        <FormControl label={T._('Camera Position', 'Admin')}>
                            <Select
                                options={
                                    cameras?.map((camera) => ({
                                        value: camera.id,
                                        label: camera.name || camera.id
                                    })) ?? []
                                }
                                value={{
                                    value: parsedAction?.get('selector') || '',
                                    label: cameras?.find((m) => m.id === parsedAction?.get('selector'))?.name || parsedAction?.get('selector') || ''
                                }}
                                onChange={(selected) => {
                                    setSelector(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return (
                    <>
                        <FormControl label={T._('Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction

                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name || s })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                            <FormControl label={T._('Material', 'Admin')}>
                                <Select
                                    options={
                                        materials?.map((material) => ({
                                            value: material.id,
                                            label: material.name || material.id
                                        })) ?? []
                                    }
                                    value={{
                                        value: (parsedAction?.get('value') as string) || '',
                                        label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                    }}
                                    onChange={(selected) => {
                                        setValue(selected?.value || '');
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return (
                    <>
                        <FormControl label={T._('Camera Position', 'Admin')}>
                            <Select
                                options={
                                    cameras?.map((camera) => ({
                                        value: camera.id,
                                        label: camera.name || camera.id
                                    })) ?? []
                                }
                                value={{
                                    value: parsedAction?.get('value') || '',
                                    label: cameras?.find((m) => m.id === parsedAction?.get('value'))?.name || parsedAction?.get('value') || ''
                                }}
                                onChange={(selected) => {
                                    setValue(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
                parsedAction.set('value', null);
                return <></>;
            default:
                return <>{T._('Unsupported action type selected.', 'Admin')}</>;
        }
    };

    return (
        <Dialog windowDecorator={ComposerSaveOptionDialogWindow} title={title} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <ComposerSaveOptionContainer>
                <div>
                    {T._('Do you need help?', 'Admin')}{' '}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-Options-and-Actions#h_01G0MHCK9C0KKE90D04WE4SVM4'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Read here', 'Admin')}
                    </Link>
                </div>
                <FormControl label={T._('Action Type', 'Admin')}>
                    <Select
                        options={ComposerActionsSelect}
                        value={ComposerActionsSelect.find((option) => option.value === actionType) || null}
                        onChange={(selected) => {
                            const id = (selected?.value as number) || ComposerOptionActionsType.SHOW_OBJECTS;
                            setActionType(id as ComposerOptionActionsType);
                            parsedAction?.set('actionTypeID', GetActionValueFromType(id as ComposerOptionActionsType));
                            parsedAction?.set('selector', '');
                            parsedAction?.set('propertyName', '');
                            parsedAction?.set('value', '');
                            update();
                        }}
                        menuPosition='fixed'
                    />
                </FormControl>
                {form()}
            </ComposerSaveOptionContainer>
        </Dialog>
    );
};

export default ComposerSaveActionDialog;
